import React, { useEffect, useMemo, useRef, useState, isValidElement } from 'react';
import { createPortal } from 'react-dom';

import { Container, Bubble } from './styles';

// Global bus to keep only one tooltip open at a time
const getTooltipBus = (): EventTarget => {
  const g: any = (globalThis as any);
  if (!g.__appTooltipBus) {
    try { g.__appTooltipBus = new EventTarget(); }
    catch {
      const listeners: { [type: string]: Function[] } = {};
      g.__appTooltipBus = {
        addEventListener: (t: string, fn: any) => { (listeners[t] ||= []).push(fn); },
        removeEventListener: (t: string, fn: any) => { listeners[t] = (listeners[t]||[]).filter(f => f !== fn); },
        dispatchEvent: (ev: any) => { (listeners[ev.type]||[]).forEach(fn => fn(ev)); return true; }
      } as unknown as EventTarget;
    }
  }
  return g.__appTooltipBus as EventTarget;
};
const tooltipBus = getTooltipBus();

interface TooltipProps {
  title: string;
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({ title, className = '', children }) => {
  const anchorRef = useRef<HTMLElement | HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const suppressRef = useRef(false);
  const idRef = useRef(Symbol('tooltip'));
  const [appearing, setAppearing] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimerRef = useRef<number | null>(null);
  const suppressTemporarily = () => {
    suppressRef.current = true;
    setTimeout(() => { suppressRef.current = false; }, 200);
  };
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const [shiftX, setShiftX] = useState(0);
  const bubbleRef = useRef<HTMLDivElement>(null);

  const isTight = useMemo(() => className?.split(' ').includes('tight'), [className]);

  const updatePosition = () => {
    const el = anchorRef.current as HTMLElement | null;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ left: rect.left + rect.width / 2, top: rect.top });
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onPointerDown = () => hide();
    const onMouseDown = () => hide();
    const onTouchStart = () => hide();
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') hide(); };
    const onVisibility = () => { if (document.hidden) hide(); };
    const onAnyOpen = (e: any) => { if (e?.detail?.id !== idRef.current) hide(); };
    const onCloseAll = () => hide();
    const onFocusIn = () => hide();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    // Hide on any document interaction to avoid sticky tooltips under overlays/modals
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('touchstart', onTouchStart, true);
    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('visibilitychange', onVisibility);
    tooltipBus.addEventListener('tooltip-open', onAnyOpen as any);
    window.addEventListener('app-tooltip-close-all', onCloseAll as any);
    window.addEventListener('focusin', onFocusIn as any, true);
    updatePosition();
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('touchstart', onTouchStart, true);
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('visibilitychange', onVisibility);
      tooltipBus.removeEventListener('tooltip-open', onAnyOpen as any);
      window.removeEventListener('app-tooltip-close-all', onCloseAll as any);
      window.removeEventListener('focusin', onFocusIn as any, true);
    };
  }, [open]);

  const show = () => {
    if (suppressRef.current) return;
    try { tooltipBus.dispatchEvent(new CustomEvent('tooltip-open', { detail: { id: idRef.current } })); } catch { /* ignore */ }
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    setAppearing(true);
    setOpen(true);
    updatePosition();
    requestAnimationFrame(() => setAppearing(false));
  };
  const hide = () => {
    if (!open && !appearing) return;
    setClosing(true);
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setClosing(false);
      closeTimerRef.current = null;
    }, 140);
  };

  useEffect(() => {
    if (open) setShiftX(0);
  }, [open, title]);

  useEffect(() => {
    if (!open) return;
    const compute = () => {
      const el = bubbleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const margin = 8;
      const vw = window.innerWidth;
      const width = rect.width;
      const leftEdge = pos.left - width / 2 + shiftX;
      const rightEdge = pos.left + width / 2 + shiftX;
      if (rightEdge > vw - margin) {
        setShiftX(prev => (vw - margin) - (pos.left + width / 2) + prev);
      } else if (leftEdge < margin) {
        setShiftX(prev => margin - (pos.left - width / 2) + prev);
      }
    };
    const raf = requestAnimationFrame(compute);
    return () => cancelAnimationFrame(raf);
  }, [open, pos.left, title, shiftX]);

  const childIsElement = isValidElement(children);
  const childDisabled = childIsElement && (children as any)?.props?.disabled;

  let trigger: React.ReactNode = children;

  if (childIsElement && !childDisabled) {
    const childEl = children as React.ReactElement<any>;
    const prevOnMouseEnter = childEl.props.onMouseEnter;
    const prevOnMouseLeave = childEl.props.onMouseLeave;
    const prevOnFocus = childEl.props.onFocus;
    const prevOnBlur = childEl.props.onBlur;
    const childRef = (childEl as any).ref;

    const mergedRef = (node: HTMLElement | null) => {
      anchorRef.current = node;
      if (typeof childRef === 'function') childRef(node);
      else if (childRef && typeof childRef === 'object') (childRef as any).current = node;
    };

    trigger = React.cloneElement(childEl, {
      onMouseEnter: (e: any) => { prevOnMouseEnter?.(e); show(); },
      onMouseLeave: (e: any) => { prevOnMouseLeave?.(e); hide(); },
      onFocus: (e: any) => { prevOnFocus?.(e); show(); },
      onBlur: (e: any) => { prevOnBlur?.(e); hide(); },
      onPointerDown: (e: any) => { suppressTemporarily(); hide(); },
      ref: mergedRef,
    });
  }

  return (
    <>
      <Container
        className={className}
        ref={anchorRef as any}
        onMouseEnter={show}
        onMouseLeave={(e) => { hide(); suppressRef.current = false; }}
        onFocus={show}
        onBlur={hide}
        onPointerEnter={show}
        onPointerLeave={() => { hide(); suppressRef.current = false; }}
        onPointerDown={() => { suppressTemporarily(); hide(); }}
      >
        {childIsElement && !childDisabled ? trigger : children}
      </Container>

      {(open || closing) && createPortal(
        <Bubble
          ref={bubbleRef}
          className={className}
          style={{
            left: pos.left,
            top: pos.top,
            transform: `translate(calc(-50% + ${shiftX}px), ${isTight ? 'calc(-100% + 12px)' : 'calc(-100% - 8px)'}) translateY(${open && !appearing ? '0px' : '12px'}) scale(${open && !appearing ? 1 : 0.98})`,
            opacity: open && !appearing ? 1 : 0,
          }}
        >
          {title}
        </Bubble>,
        document.body
      )}
    </>
  );
};

export default Tooltip;
